import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import analyticsService from '../../services/analyticsService';

const Login = ({ onSuccess, onSwitchToRegister, onDismiss }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null);
  const [resendStatus, setResendStatus] = useState(null);

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setErrors({});
    try {
      await loginWithGoogle(credentialResponse.credential);
      analyticsService.trackLogin('google');
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrors({ submit: error.message || 'Error al iniciar sesión con Google' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    return newErrors;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validar formulario
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Llamar a la función de login del contexto
      await login(formData.email, formData.password);
      analyticsService.trackLogin('email');

      // Limpiar el formulario
      setFormData({
        email: '',
        password: '',
      });

      // Cerrar el modal después de un inicio de sesión exitoso
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error en el inicio de sesión:', error);
      if (error.requiresVerification) {
        analyticsService.trackEvent('user', 'login_blocked', 'email_not_verified', formData.email);
        setPendingVerification(error.verificationEmail || formData.email);
        setErrors({});
      } else {
        setErrors({
          submit: error.message || 'Error al iniciar sesión. Por favor, verifica tus credenciales.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendFromLogin = async () => {
    if (!pendingVerification) return;
    setResendStatus('loading');
    try {
      await authService.resendVerification(pendingVerification);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  // Sin tarjeta ni título propios: los aporta el contenedor (AuthModal o
  // LoginPage) a través de AuthPanel, que ya rotula la pestaña activa.
  return (
    <div>
      {pendingVerification && (
        <div className="mb-4 bg-df-warning/10 border border-df-warning rounded-md p-4">
          <p className="text-sm font-medium text-df-warning mb-1">Correo no verificado</p>
          <p className="text-sm text-df-warning mb-2">
            Debes verificar tu correo <span className="font-semibold">{pendingVerification}</span> antes de iniciar sesión.
          </p>
          {resendStatus === 'sent' ? (
            <p className="text-sm text-df-success">Correo de verificación reenviado.</p>
          ) : resendStatus === 'error' ? (
            <p className="text-sm text-df-danger">Error al reenviar. Intenta de nuevo.</p>
          ) : (
            <button
              type="button"
              onClick={handleResendFromLogin}
              disabled={resendStatus === 'loading'}
              className="text-sm text-df-warning underline hover:opacity-80 disabled:opacity-50"
            >
              {resendStatus === 'loading' ? 'Reenviando...' : 'Reenviar correo de verificación'}
            </button>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.submit && (
          <div className="bg-df-danger/10 border border-df-danger text-df-danger px-4 py-3 rounded relative">
            <span className="block sm:inline">{errors.submit}</span>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-df-text mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-df-text focus:outline-none focus:ring-2 focus:ring-df-accent ${
              errors.email ? 'border-df-danger' : 'border-df-border'
            }`}
            placeholder="tu@email.com"
          />
          {errors.email && <p className="mt-1 text-sm text-df-danger">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-df-text mb-1">
            Contraseña
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-df-text focus:outline-none focus:ring-2 focus:ring-df-accent ${
              errors.password ? 'border-df-danger' : 'border-df-border'
            }`}
            placeholder="••••••••"
          />
          {errors.password && <p className="mt-1 text-sm text-df-danger">{errors.password}</p>}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-df-accent-hover focus:ring-df-accent border-df-border rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-df-text">
              Recordarme
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={() => {
                // onDismiss cierra el modal si venimos de él; en /login no
                // hay nada que cerrar. No se usa onSuccess: no hubo login.
                if (onDismiss) onDismiss();
                navigate('/forgot-password');
              }}
              className="font-medium text-df-accent-hover hover:opacity-80"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-df-accent-strong hover:bg-df-accent-deep focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-df-accent disabled:opacity-50"
          >
            {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </button>
        </div>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-df-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-df-surface text-df-muted">O continúa con</span>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setErrors({ submit: 'Error al iniciar con Google' })}
            text="signin_with"
            shape="rectangular"
            width="100%"
            locale="es"
          />
        </div>

        {googleLoading && (
          <p className="mt-2 text-center text-sm text-df-muted">Conectando con Google...</p>
        )}

      </div>

      <div className="mt-4 text-center">
        <p className="text-sm text-df-muted">
          ¿No tienes una cuenta?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="text-df-accent-hover hover:text-df-accent font-medium"
          >
            Regístrate aquí
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
